import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import ForceGraph3DLib from 'react-force-graph-3d';
import * as THREE from 'three';

const NODE_COLORS = {
  SERVICE: '#3b82f6',
  TEAM: '#22c55e',
  PATTERN: '#a855f7',
  API: '#eab308',
  REGULATION: '#ef4444',
  DOCUMENT: '#06b6d4',
  CAPABILITY: '#f97316',
  PRODUCT: '#ec4899',
  DEFAULT: '#6b7280',
};

const LINK_COLORS = {
  DEPENDS_ON: '#6b7280',
  OWNED_BY: '#22c55e',
  USES_PATTERN: '#a855f7',
  COMPLIES_WITH: '#ef4444',
  INTEGRATES_WITH: '#3b82f6',
  DOCUMENTS: '#06b6d4',
  MENTIONS: '#9ca3af',
  DEFAULT: '#4a4a4a',
};

function ForceGraph3D({ entities, relationships, selectedEntity, onSelectEntity, width, height }) {
  const fgRef = useRef();

  const graphData = useMemo(() => {
    const nodes = entities.map(entity => ({
      id: entity.id,
      name: entity.name,
      type: entity.entity_type,
      description: entity.description,
      val: entity.entity_type === 'SERVICE' ? 3 : 2, // Node size
      color: NODE_COLORS[entity.entity_type] || NODE_COLORS.DEFAULT,
      ...entity,
    }));

    const nodeIds = new Set(nodes.map(n => n.id));
    const links = relationships
      .filter(rel => nodeIds.has(rel.source_id) && nodeIds.has(rel.target_id))
      .map(rel => ({
        source: rel.source_id,
        target: rel.target_id,
        type: rel.rel_type,
        description: rel.description,
        color: LINK_COLORS[rel.rel_type] || LINK_COLORS.DEFAULT,
      }));

    return { nodes, links };
  }, [entities, relationships]);

  // Focus on selected node
  useEffect(() => {
    if (selectedEntity && fgRef.current) {
      const node = graphData.nodes.find(n => n.id === selectedEntity);
      if (node && node.x !== undefined) {
        const distance = 150;
        const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z);
        fgRef.current.cameraPosition(
          { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio },
          node,
          1000
        );
      }
    }
  }, [selectedEntity, graphData.nodes]);

  // Custom node rendering with small labeled spheres
  const nodeThreeObject = useCallback((node) => {
    const isSelected = node.id === selectedEntity;
    const group = new THREE.Group();

    // Small sphere
    const radius = isSelected ? 4 : 3;
    const geometry = new THREE.SphereGeometry(radius, 16, 16);
    const material = new THREE.MeshLambertMaterial({
      color: node.color,
      transparent: true,
      opacity: 0.95,
    });
    const sphere = new THREE.Mesh(geometry, material);
    group.add(sphere);

    // Add glow ring for selected node
    if (isSelected) {
      const ringGeometry = new THREE.RingGeometry(5, 7, 32);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: '#a855f7',
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      group.add(ring);
    }

    // Compact label sprite
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: createTextTexture(node.name, node.type),
        transparent: true,
      })
    );
    sprite.scale.set(24, 8, 1);
    sprite.position.set(0, 6, 0);
    group.add(sprite);

    return group;
  }, [selectedEntity]);

  // Create compact text texture for labels
  const createTextTexture = (text, type) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 64;

    // Semi-transparent background pill
    context.fillStyle = 'rgba(10, 10, 10, 0.85)';
    context.beginPath();
    context.roundRect(4, 4, 248, 56, 28);
    context.fill();

    // Colored left edge indicator
    context.fillStyle = NODE_COLORS[type] || NODE_COLORS.DEFAULT;
    context.beginPath();
    context.arc(28, 32, 12, 0, Math.PI * 2);
    context.fill();

    // Type letter inside circle
    context.fillStyle = '#ffffff';
    context.font = 'bold 16px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(type?.charAt(0) || '?', 28, 33);

    // Name text
    context.fillStyle = '#e5e5e5';
    context.font = '18px Arial, sans-serif';
    context.textAlign = 'left';
    const displayName = text?.length > 14 ? text.substring(0, 14) + '..' : text;
    context.fillText(displayName || '', 48, 33);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  };

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    onSelectEntity(node.id);
  }, [onSelectEntity]);

  // Handle background click
  const handleBackgroundClick = useCallback(() => {
    onSelectEntity(null);
  }, [onSelectEntity]);

  return (
    <ForceGraph3DLib
      ref={fgRef}
      width={width}
      height={height}
      graphData={graphData}
      nodeThreeObject={nodeThreeObject}
      nodeThreeObjectExtend={false}
      linkColor={link => link.color}
      linkOpacity={0.6}
      linkWidth={1.5}
      linkDirectionalArrowLength={6}
      linkDirectionalArrowRelPos={0.9}
      linkDirectionalArrowColor={link => link.color}
      linkCurvature={0.1}
      onNodeClick={handleNodeClick}
      onBackgroundClick={handleBackgroundClick}
      backgroundColor="#0a0a0a"
      showNavInfo={false}
      enableNodeDrag={true}
      enableNavigationControls={true}
      controlType="orbit"
      d3VelocityDecay={0.3}
      d3AlphaDecay={0.02}
      cooldownTicks={100}
      warmupTicks={50}
    />
  );
}

export default ForceGraph3D;
